import { PermissionCode, PermissionType, PlanPriceInterval } from '@/graphql/generated/types.generated';
import { FEATURED_CODES } from '../constants';
import type { MappedFeature, MappedPlan } from '../utils/map-graphql-to-calculator.util';
import type { AddOnLine } from '../utils/calc-total-price.util';
import { resolveInitialAddOns } from '../utils/clamp-addon-value.util';
import { useCallback, useEffect, useRef, useState } from 'react';

export type { AddOnLine } from '../utils/calc-total-price.util';

export interface CalculatorInput {
    plans: MappedPlan[];
    onIntervalChange: (val: PlanPriceInterval) => void;
    initialPlanCode?: string;
    featuredCodes?: PermissionCode[];
    /** Pre-set add-on values (code → value); clamped & validated before seeding. */
    initialAddOns?: Record<string, string | number | boolean>;
}

export interface CalculatorState {
    selectedPlan: MappedPlan | undefined;
    addOnQty: Record<string, number>;
    addOnBooleans: Record<string, boolean>;
    addOnEnums: Record<string, string>;
    viewMore: boolean;
    featuredFeatures: MappedFeature[];
    featureByCode: Record<string, MappedFeature>;
    priceSummaryLines: AddOnLine[];
    /** Feature codes in the order the user first activated them (drives summary order). */
    activationOrder: string[];
    handlePlanSelect: (code: string) => void;
    handleIntervalChange: (val: PlanPriceInterval) => void;
    handleAddOnChange: (code: string, qty: number) => void;
    handleBoolChange: (code: string, enabled: boolean) => void;
    handleEnumChange: (code: string, value: string) => void;
    setViewMore: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useCalculatorState = ({
    plans,
    onIntervalChange,
    initialPlanCode,
    featuredCodes = FEATURED_CODES,
    initialAddOns,
}: CalculatorInput): CalculatorState => {
    const [selectedCode, setSelectedCode] = useState<string>(initialPlanCode ?? '');
    const [addOnQty, setAddOnQty] = useState<Record<string, number>>({});
    const [addOnBooleans, setAddOnBooleans] = useState<Record<string, boolean>>({});
    const [addOnEnums, setAddOnEnums] = useState<Record<string, string>>({});
    const [activationOrder, setActivationOrder] = useState<string[]>([]);
    const [viewMore, setViewMore] = useState(false);

    // Until the user picks a plan (or `initialPlanCode` matches), default to the first paid
    // plan — the hook mounts before plan data loads, so this can't be a static initial value.
    const selectedPlan =
        plans.find((p) => p.code === selectedCode) ?? plans.find((p) => p.monthlyPrice > 0) ?? plans[0];

    // Resolve `initialAddOns` against a plan and load them (clamped & validated). Used on the
    // first data load and again on every plan switch, so the seeded add-ons persist across
    // plan changes instead of being cleared.
    const applyInitialAddOns = useCallback(
        (plan: MappedPlan | undefined) => {
            const { qty, booleans, enums } = resolveInitialAddOns(plan?.features ?? [], initialAddOns);
            setAddOnQty(qty);
            setAddOnBooleans(booleans);
            setAddOnEnums(enums);
            const seededCodes = [...Object.keys(qty), ...Object.keys(booleans), ...Object.keys(enums)];
            if (seededCodes.some((c) => !featuredCodes.includes(c as PermissionCode))) {
                setViewMore(true);
            }
        },
        [initialAddOns, featuredCodes],
    );

    // Seed once after plan data is available — the hook mounts before the GraphQL data loads
    // (empty plans), so this can't be a lazy useState initializer.
    const seededRef = useRef(false);
    useEffect(() => {
        if (seededRef.current || !initialAddOns) return;
        if (!selectedPlan || selectedPlan.features.length === 0) return;
        applyInitialAddOns(selectedPlan);
        seededRef.current = true;
    }, [selectedPlan, initialAddOns, applyInitialAddOns]);

    const handlePlanSelect = (code: string) => {
        setSelectedCode(code);
        setAddOnQty({});
        setAddOnBooleans({});
        setAddOnEnums({});
        setActivationOrder([]);
        applyInitialAddOns(plans.find((p) => p.code === code) ?? plans[0]);
    };

    /** Appends `code` on first activation, drops it when deactivated — keeps insertion order. */
    const updateActivation = (code: string, active: boolean) => {
        setActivationOrder((prev) => {
            const has = prev.includes(code);
            if (active && !has) return [...prev, code];
            if (!active && has) return prev.filter((c) => c !== code);
            return prev;
        });
    };

    // Featured features pull from the selected plan's feature list (has the plan's base value)
    const featuredFeatures = featuredCodes
        .map((code) => selectedPlan?.features.find((f) => f.code === code))
        .filter((f): f is MappedFeature => f !== undefined);

    // featureByCode is keyed from the selected plan so values are plan-specific
    const featureByCode: Record<string, MappedFeature> = Object.fromEntries(
        selectedPlan?.features.map((f) => [f.code, f]) ?? [],
    );

    /** Returns true when setting the add-on override to `newValue` causes the
     *  feature to become inactive (so its dependents should be cleared). */
    const wouldBecomeInactive = (code: string, newValue: number | boolean | string): boolean => {
        const feature = featureByCode[code];
        if (!feature) return false;
        switch (feature.type) {
            case PermissionType.Boolean:
                return feature.value !== 1 && !newValue;
            case PermissionType.Numeric:
                return feature.value <= 0 && Number(newValue) <= 0;
            case PermissionType.Enum:
                return feature.displayValue === '' && newValue === '';
            default:
                return false;
        }
    };

    /** Resets add-on state for all features whose `dependsOn` is `parentCode`. */
    const clearChildrenOf = (parentCode: string) => {
        const childCodes = (selectedPlan?.features ?? [])
            .filter((f) => f.dependsOn === parentCode)
            .map((f) => f.code);
        if (childCodes.length === 0) return;
        setAddOnQty((prev) => {
            const next = { ...prev };
            childCodes.forEach((c) => delete next[c]);
            return next;
        });
        setAddOnBooleans((prev) => {
            const next = { ...prev };
            childCodes.forEach((c) => delete next[c]);
            return next;
        });
        setAddOnEnums((prev) => {
            const next = { ...prev };
            childCodes.forEach((c) => delete next[c]);
            return next;
        });
    };

    const handleIntervalChange = (val: PlanPriceInterval) => onIntervalChange(val);
    const handleAddOnChange = (code: string, qty: number) => {
        setAddOnQty((prev) => ({ ...prev, [code]: qty }));
        updateActivation(code, qty > 0);
        if (wouldBecomeInactive(code, qty)) clearChildrenOf(code);
    };
    const handleBoolChange = (code: string, enabled: boolean) => {
        setAddOnBooleans((prev) => ({ ...prev, [code]: enabled }));
        updateActivation(code, enabled);
        if (wouldBecomeInactive(code, enabled)) clearChildrenOf(code);
    };
    const handleEnumChange = (code: string, value: string) => {
        setAddOnEnums((prev) => {
            const next = { ...prev, [code]: value };
            // #7 — image + video upload size are one bundle: mirror the video tier to the
            // image tier (matched by option order) so video isn't an independent free upgrade.
            if (code === PermissionCode.UploadSizeImages) {
                const imgOptions = featureByCode[code]?.addOnOptions ?? [];
                const videoFeature = featureByCode[PermissionCode.UploadSizeVideos];
                const videoOptions = videoFeature?.addOnOptions ?? [];
                const idx = imgOptions.findIndex((o) => o.value === value);
                // Some plans already grant a video tier above their image tier, and the backend
                // rejects an enum downgrade — never mirror below what the plan already includes.
                const includedIdx = videoOptions.findIndex((o) => o.value === videoFeature?.displayValue);
                const mirroredVideo = videoOptions[Math.max(idx, includedIdx)]?.value;
                if (videoFeature && mirroredVideo) {
                    next[PermissionCode.UploadSizeVideos] = mirroredVideo;
                }
            }
            return next;
        });
        const baseValue = featureByCode[code]?.displayValue ?? '';
        updateActivation(code, value !== '' && value !== baseValue);
        if (wouldBecomeInactive(code, value)) clearChildrenOf(code);
    };

    const priceSummaryLines: AddOnLine[] = (selectedPlan?.features ?? []).map((f) => ({
        feature: f,
        extraQty: addOnQty[f.code] ?? 0,
        boolEnabled: addOnBooleans[f.code] ?? false,
        enumValue: addOnEnums[f.code] ?? f.displayValue,
    }));

    return {
        selectedPlan,
        addOnQty,
        addOnBooleans,
        addOnEnums,
        viewMore,
        featuredFeatures,
        featureByCode,
        priceSummaryLines,
        activationOrder,
        handlePlanSelect,
        handleIntervalChange,
        handleAddOnChange,
        handleBoolChange,
        handleEnumChange,
        setViewMore,
    };
};
